import React from 'react';
import Layout from '../../components/Layout';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';

const ImpactPage = () => {
  const impactThemes = [
    {
      icon: 'Building2',
      title: 'Formalizing Microbusinesses',
      description: 'Transitioning informal service providers into the formal economy with proper registration, documentation, and tax compliance.',
      metric: '8,400+',
      metricLabel: 'businesses formalized',
    },
    {
      icon: 'Calendar',
      title: 'Improving Appointment Density',
      description: 'Reducing no-shows and double-bookings while maximizing productive hours for service providers.',
      metric: '34%',
      metricLabel: 'increase in bookings',
    },
    {
      icon: 'CreditCard',
      title: 'Digital Banking Access',
      description: 'Connecting previously unbanked service providers with digital payment tools and financial services.',
      metric: '6,200+',
      metricLabel: 'first-time digital recipients',
    },
    {
      icon: 'Users',
      title: 'Local Job Creation',
      description: 'Creating employment opportunities across markets through platform operations and vendor support.',
      metric: '340+',
      metricLabel: 'direct jobs created',
    },
    {
      icon: 'Heart',
      title: 'Women-Led Businesses',
      description: 'Supporting women entrepreneurs in traditionally underserved sectors like beauty, wellness, and home services.',
      metric: '62%',
      metricLabel: 'women-owned vendors',
    },
    {
      icon: 'GraduationCap',
      title: 'Financial Literacy',
      description: 'Providing training and resources to help vendors manage their finances and grow their businesses.',
      metric: '4,800+',
      metricLabel: 'vendors trained',
    },
  ];

  const esgMetrics = [
    { category: 'Social', items: ['Gender inclusion (62% women vendors)', 'Business training access', 'Financial literacy programs', 'Youth employment initiatives'] },
    { category: 'Economic', items: ['Increased income visibility', 'Digital receipt generation', 'Tax compliance support', 'Access to credit history'] },
    { category: 'Environmental', items: ['Route optimization', 'Reduced travel emissions', 'Digital-first operations', 'Paperless transactions'] },
  ];

  const caseStudies = [
    {
      name: 'Maria\'s Beauty Salon',
      location: 'Santo Domingo, DR',
      category: 'Beauty & Wellness',
      before: { revenue: '$800/mo', bookings: '~20/mo', payments: 'Cash only' },
      after: { revenue: '$2,400/mo', bookings: '65/mo', payments: 'Digital + Cash' },
      highlights: ['200% revenue increase', '3x more bookings', 'First business loan approved'],
    },
    {
      name: 'Carlos Home Services',
      location: 'New York, NY',
      category: 'Home Services',
      before: { revenue: '$1,200/mo', bookings: '~15/mo', payments: 'Cash/Venmo' },
      after: { revenue: '$4,100/mo', bookings: '52/mo', payments: 'Full digital' },
      highlights: ['242% revenue increase', 'Hired 2 employees', 'Registered LLC'],
    },
    {
      name: 'Ana\'s Tutoring',
      location: 'Miami, FL',
      category: 'Education',
      before: { revenue: '$600/mo', bookings: '~12/mo', payments: 'Cash only' },
      after: { revenue: '$1,800/mo', bookings: '38/mo', payments: 'Digital + recurring' },
      highlights: ['200% revenue increase', 'Subscription model', 'Tax compliant'],
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-medium text-accent uppercase tracking-wider mb-4">
              Impact & Economic Development
            </p>
            <h1 className="text-hero text-foreground mb-6">
              Transforming Communities Through Technology
            </h1>
            <p className="text-body-large">
              Our platform doesn't just enable transactions—it creates pathways to economic opportunity,
              financial inclusion, and sustainable growth for micro-businesses across Latin America.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Themes */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-display text-foreground mb-6">
              Impact Themes
            </h2>
            <p className="text-body-large">
              Our work creates measurable impact across multiple dimensions of economic and social development.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {impactThemes.map((theme, index) => (
              <div key={index} className="bg-white rounded-brand p-6 border border-border">
                <div className="w-12 h-12 bg-accent/10 rounded-brand-sm flex items-center justify-center mb-4">
                  <Icon name={theme.icon} size={24} className="text-accent" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {theme.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {theme.description}
                </p>
                <div className="pt-4 border-t border-border">
                  <div className="text-2xl font-semibold text-primary">{theme.metric}</div>
                  <div className="text-xs text-muted-foreground">{theme.metricLabel}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ESG Metrics */}
      <section id="esg" className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-display text-foreground mb-6">
                ESG Metrics
              </h2>
              <p className="text-body-large mb-8">
                We track and report on environmental, social, and governance metrics
                to demonstrate our commitment to responsible growth.
              </p>
              <Link
                to="/investor-relations"
                className="inline-flex items-center gap-2 text-primary font-medium hover:text-primary-light transition-colors"
              >
                Download full ESG report
                <Icon name="Download" size={16} />
              </Link>
            </div>

            <div className="space-y-6">
              {esgMetrics.map((metric, index) => (
                <div key={index} className="bg-muted rounded-brand p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    {metric.category}
                  </h3>
                  <ul className="space-y-2">
                    {metric.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Icon name="Check" size={14} className="text-accent" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section id="case-studies" className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-display text-foreground mb-6">
              Case Studies
            </h2>
            <p className="text-body-large">
              Real stories of transformation from vendors using our platform.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-white rounded-brand-lg overflow-hidden border border-border">
                <div className="bg-primary p-6 text-white">
                  <div className="text-xs font-medium text-white/60 uppercase tracking-wider mb-2">
                    {study.category}
                  </div>
                  <h3 className="text-xl font-semibold mb-1">{study.name}</h3>
                  <p className="text-sm text-white/70">{study.location}</p>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Before UE</div>
                      <div className="space-y-1 text-sm">
                        <div><span className="text-muted-foreground">Revenue:</span> {study.before.revenue}</div>
                        <div><span className="text-muted-foreground">Bookings:</span> {study.before.bookings}</div>
                        <div><span className="text-muted-foreground">Payments:</span> {study.before.payments}</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-accent uppercase tracking-wider mb-2">After UE</div>
                      <div className="space-y-1 text-sm">
                        <div><span className="text-muted-foreground">Revenue:</span> <span className="font-medium text-foreground">{study.after.revenue}</span></div>
                        <div><span className="text-muted-foreground">Bookings:</span> <span className="font-medium text-foreground">{study.after.bookings}</span></div>
                        <div><span className="text-muted-foreground">Payments:</span> <span className="font-medium text-foreground">{study.after.payments}</span></div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Key Outcomes</div>
                    <div className="flex flex-wrap gap-2">
                      {study.highlights.map((highlight, i) => (
                        <span key={i} className="px-2 py-1 bg-accent/10 text-accent text-xs rounded">
                          {highlight}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-wide mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-display text-white mb-6">
            Partner with us to create impact
          </h2>
          <p className="text-white/70 mb-8 max-w-2xl mx-auto">
            Join our mission to formalize and empower micro-businesses across Latin America.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/partnerships"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-primary font-medium rounded-brand-sm hover:bg-white/90 transition-colors"
            >
              Become a Partner
              <Icon name="ArrowRight" size={16} />
            </Link>
            <Link
              to="/investor-relations"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-white/30 text-white font-medium rounded-brand-sm hover:bg-white/10 transition-colors"
            >
              Investor Information
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ImpactPage;
